"use client";

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  MoreHorizontal,
  Check,
  ChevronDown,
  ArrowLeft,
  History,
  Monitor,
  Smartphone,
  ExternalLink,
  PanelLeftOpen,
  PanelLeftClose,
  RotateCw,
  X,
} from "lucide-react";
import {
  items,
  platform,
  uploadDesignAsset,
  type DesignAIWallet,
  type DesignSaveOperation,
  type DesignChange,
  type DesignContent,
  type DesignConversation,
  type DesignConversationEvent,
  type DesignRelease,
  type DesignSession,
  type DesignWebsite,
} from "./api";
import css from "./PersistentEditor.module.css";
import { EditorChat, useChatAttachments } from "./EditorChat";
import { PublishedVersions } from "./PublishedVersions";
import { WebsiteDraftHistory } from "./draft-history";
import {
  validWebsiteText,
  type WebsiteEditorFrameMessage,
} from "@open-design/contracts";
import { Button, Input, Textarea } from "./ui";

const SAFE_PAGE = /^\/(?:[A-Za-z0-9_-]+\/)*$/;
const RUNNING = new Set([
  "QUEUED",
  "PREPARING",
  "EDITING",
  "CHECKING",
  "NEEDS_INPUT",
]);
const labels: Record<string, string> = {
  QUEUED: "Starting workspace",
  PREPARING: "Starting workspace",
  EDITING: "Making changes",
  CHECKING: "Saving/checking",
  NEEDS_INPUT: "Needs your input",
  READY: "Ready for review",
  FAILED: "Could not complete this change",
  CANCELLED: "Stopped",
};
const message = (error: unknown) =>
  error instanceof Error
    ? error.message
    : "The request could not be completed.";

export function PersistentEditor({
  websiteId,
  org,
  session,
  onBack,
  onNavigationDirty,
  onWebsiteUpdated,
  navigationControl,
  previewOnly = false,
  initialPage = "/",
  onOpenHistory,
  onOpenSettings,
  onPageChange,
  unifiedSave = false,
}: {
  websiteId: string;
  org: number;
  session: DesignSession;
  onBack: () => void;
  onNavigationDirty?: (dirty: boolean) => void;
  onWebsiteUpdated?: (website: DesignWebsite) => void;
  navigationControl?: ReactNode;
  previewOnly?: boolean;
  initialPage?: string;
  onOpenHistory?: () => void;
  onOpenSettings?: () => void;
  onPageChange?: (page: string) => void;
  unifiedSave?: boolean;
}) {
  const [aiWallet, setAiWallet] = useState<DesignAIWallet | null>(null);
  const [aiWalletError, setAiWalletError] = useState(false);
  const [site, setSite] = useState<DesignWebsite | null>(null);
  const [content, setContent] = useState<DesignContent | null>(null);
  const saveFlow = unifiedSave && !previewOnly && session.save_flow === true;
  const [saveOperation, setSaveOperation] =
    useState<DesignSaveOperation | null>(null);
  const savePending =
    saveOperation?.status === "CHECKING" ||
    saveOperation?.status === "PUBLISHING";
  const [draft, setDraft] = useState<Record<string, string>>({});
  const history = useRef(new WebsiteDraftHistory());
  const [historyRevision, setHistoryRevision] = useState(0);
  const draftRevision = useRef(0);
  const [enhanced, setEnhanced] = useState(false);
  const [flushing, setFlushing] = useState(false);
  const [composing, setComposing] = useState(false);
  const [panelOpen, setPanelOpen] = useState(false);
  const [toolsCollapsed, setToolsCollapsed] = useState(false);
  const [narrow, setNarrow] = useState(false);
  const [sheetOffset, setSheetOffset] = useState(0);
  const [sheetExpanded, setSheetExpanded] = useState(false);
  const sheetGesture = useRef<{ id: number; y: number; time: number } | null>(
    null,
  );
  const sheetMoved = useRef(false);
  useEffect(() => {
    if (!panelOpen || !narrow) {
      setSheetExpanded(false);
      setSheetOffset(0);
      sheetGesture.current = null;
    }
  }, [panelOpen, narrow]);

  useEffect(() => {
    if (typeof matchMedia === "undefined") return;
    const query = matchMedia("(max-width:1023px)");
    const update = () => setNarrow(query.matches);
    update();
    query.addEventListener("change", update);
    return () => query.removeEventListener("change", update);
  }, []);
  const [publishMenu, setPublishMenu] = useState(false);
  const [restoreOpen, setRestoreOpen] = useState(false);
  const [aiContext, setAiContext] = useState<string | null>(null);
  const pendingAction = useRef(false);
  const [relatedIds, setRelatedIds] = useState<string[]>([]);
  const receivedSequence = useRef(0);
  const flushWaiter = useRef<{
    id: string;
    resolve: () => void;
    reject: (error: Error) => void;
    timer: ReturnType<typeof setTimeout>;
  } | null>(null);
  const [changes, setChanges] = useState<DesignChange[]>([]);
  const [releases, setReleases] = useState<DesignRelease[]>([]);
  const [events, setEvents] = useState<DesignConversationEvent[]>([]);
  const [error, setError] = useState("");
  const [refreshError, setRefreshError] = useState("");
  const [accessDenied, setAccessDenied] = useState(false);
  const [busy, setBusy] = useState(false);
  const [prompt, setPrompt] = useState("");
  const attachments = useChatAttachments(websiteId, org, session.csrf_token);
  const references = attachments.files
    .filter((file) => file.status === "ready")
    .map((file) => ({ id: file.id!, name: file.file.name }));
  const [tab, setTab] = useState<"chat" | "content">("chat");
  const [page] = useState(SAFE_PAGE.test(initialPage) ? initialPage : "/");
  const [actionsOpen, setActionsOpen] = useState(false);
  const actionsRoot = useRef<HTMLDivElement>(null);
  const actionsTrigger = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!actionsOpen) return;
    actionsRoot.current
      ?.querySelector<HTMLElement>('[role="menuitem"]')
      ?.focus();
    const close = (event: PointerEvent) => {
      if (!actionsRoot.current?.contains(event.target as Node))
        setActionsOpen(false);
    };
    document.addEventListener("pointerdown", close);
    return () => document.removeEventListener("pointerdown", close);
  }, [actionsOpen]);
  const [pagePickerOpen, setPagePickerOpen] = useState(false);
  const [pageSearch, setPageSearch] = useState("");
  const pagePickerRoot = useRef<HTMLDivElement>(null);
  const pagePickerTrigger = useRef<HTMLButtonElement>(null);
  const pageOptions = [
    ...new Set([
      page,
      ...(content?.manifest?.pages ??
        content?.manifest?.fields.map((field) => field.page) ??
        []),
    ]),
  ]
    .filter((path) => SAFE_PAGE.test(path))
    .sort((a, b) => (a === "/" ? -1 : b === "/" ? 1 : a.localeCompare(b)));
  const pageName = (path: string) =>
    path === "/"
      ? "Home"
      : path
          .split("/")
          .filter(Boolean)
          .at(-1)!
          .split(/[-_]/)
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(" ");
  useEffect(() => {
    if (!pagePickerOpen) return;
    const root = pagePickerRoot.current;
    (
      root?.querySelector<HTMLElement>('input, [aria-checked="true"]') ??
      root?.querySelector<HTMLElement>('[role="menuitemradio"]')
    )?.focus();
    const close = (event: PointerEvent) => {
      if (!root?.contains(event.target as Node)) setPagePickerOpen(false);
    };
    document.addEventListener("pointerdown", close);
    return () => document.removeEventListener("pointerdown", close);
  }, [pagePickerOpen]);
  const [selected, setSelected] = useState("");
  const [historical, setHistorical] = useState<string | null>(null);
  const [mobile, setMobile] = useState(false);
  const [frameRevision, setFrameRevision] = useState(0);
  const [frameEpoch, setFrameEpoch] = useState(0);
  const [loadedFrameKey, setLoadedFrameKey] = useState<string | null>(null);
  const frameLoadAttempts = useRef(0);
  const frame = useRef<HTMLIFrameElement>(null);
  const dirtyRef = useRef(false);
  const navigationDirty = useRef(false);
  const publishRoot = useRef<HTMLDivElement>(null);
  const panelTrigger = useRef<HTMLButtonElement>(null);
  const chatPane = useRef<HTMLDivElement>(null);
  const publishTrigger = useRef<HTMLButtonElement>(null);
  const loadedVersion = useRef(-1);
  const loadedPreview = useRef<string | null | undefined>(undefined);
  const cursor = useRef(0);
  const alive = useRef(true);
  const loading = useRef(false);
  const pending = useRef<{ signature: string; key: string } | null>(null);
  const organization = session.organizations.find((value) => value.id === org);
  const canEdit =
    organization?.can_edit === true &&
    site?.can_write !== false &&
    !accessDenied;
  const canPublish =
    organization?.can_publish === true &&
    site?.can_write !== false &&
    !accessDenied;
  const base = `websites/${websiteId}/`;
  const dirty =
    composing ||
    Object.keys(draft).some((key) => draft[key] !== content?.values?.[key]);
  dirtyRef.current = dirty;
  navigationDirty.current =
    dirty || !!prompt.trim() || attachments.files.length > 0;
  const navigationHasDraft = navigationDirty.current;
  useEffect(() => {
    onNavigationDirty?.(navigationHasDraft);
  }, [navigationHasDraft, onNavigationDirty]);
  useEffect(() => () => onNavigationDirty?.(false), [onNavigationDirty]);
  const active = changes.find((change) => RUNNING.has(change.status));
  const aiAllowed =
    !session.ai_wallet ||
    (!aiWalletError &&
      !!aiWallet &&
      (active?.status === "NEEDS_INPUT"
        ? aiWallet.can_resume
        : aiWallet.can_request));
  const publicationBusy = ["REQUESTED", "PUBLISHING"].includes(
    site?.publication?.status ?? "",
  );
  const requestedRelease = releases.find(
    (release) => release.id === (historical ?? site?.preview_release),
  );
  const currentRelease = requestedRelease?.review_url
    ? requestedRelease
    : historical
      ? undefined
      : releases.find((release) => !!release.review_url);
  const origin = currentRelease?.review_url
    ? new URL(currentRelease.review_url).origin
    : null;
  // Mount only after the page's content contract is available, so its first
  // connection can bind to the correct revision and editing permissions.
  const frameUrl =
    currentRelease?.review_url && content
      ? new URL(
          `${SAFE_PAGE.test(page) ? page : "/"}?imEditor=1`,
          currentRelease.review_url,
        ).href
      : null;
  const frameKey = `${frameUrl}:${frameRevision}`;
  const frameLoaded = loadedFrameKey === frameKey;
  function previewDigestKey(value: DesignContent | null) {
    return value?.preview_content_digest ?? value?.content_digest;
  }
  function updateHistoryView() {
    setDraft(history.current.current.values);
    dirtyRef.current = true;
    draftRevision.current += 1;
    setHistoryRevision(draftRevision.current);
  }
  function resetDraft(values: Record<string, string>) {
    history.current.reset(values);
    setDraft(history.current.current.values);
    draftRevision.current += 1;
    setHistoryRevision(draftRevision.current);
  }
  function editField(
    id: string,
    value: string,
    asset?: { id: string; url: string },
  ) {
    const field = content?.manifest?.fields.find((item) => item.id === id);
    if (!field || !editable || flushing) return;
    if (
      ["text", "multiline"].includes(field.type) &&
      !validWebsiteText(value)
    ) {
      setError("Use plain text up to 20,000 characters.");
      return;
    }
    if (
      history.current.change(
        id,
        value,
        asset,
        ["image", "link"].includes(field.type),
      )
    )
      updateHistoryView();
  }
  function travel(direction: "undo" | "redo") {
    if (!editable || flushing || composing) return;
    if (history.current[direction]()) updateHistoryView();
  }
  function inspectorKey(event: React.KeyboardEvent) {
    if (event.nativeEvent.isComposing) return;
    if (!(event.target as HTMLElement).closest("[data-draft-field]")) return;
    if (
      !(event.ctrlKey || event.metaKey) ||
      !["z", "y"].includes(event.key.toLowerCase())
    )
      return;
    event.preventDefault();
    travel(event.shiftKey || event.key.toLowerCase() === "y" ? "redo" : "undo");
  }
  const currentPreview =
    !historical && currentRelease?.source_version === content?.source_version;
  const previewDigest =
    content?.preview_content_digest ?? content?.content_digest;
  const editable =
    canEdit &&
    content?.enabled === true &&
    !historical &&
    currentRelease?.id === site?.preview_release &&
    (currentPreview || content.preview_bindings_compatible === true) &&
    !active &&
    !savePending &&
    !publicationBusy &&
    !busy &&
    site?.source_version === content.source_version;
  // A temporary pause (for example an upload) disables the embedded runtime.
  // Re-enable it with a fresh identity because its ordered message stream restarts.
  const editingSession = useMemo(
    () => crypto.randomUUID(),
    [
      frameKey,
      frameEpoch,
      content?.source_version,
      previewDigestKey(content),
      editable,
    ],
  );
  const fields = content?.manifest?.fields ?? [];
  const selectedField = fields.find((field) => field.id === selected);

  const refresh = useCallback(async () => {
    if (loading.current) return;
    loading.current = true;
    try {
      const [nextSite, nextChanges, nextReleases, history, nextSaves] =
        await Promise.all([
          platform<DesignWebsite>(base, { org }),
          platform<DesignChange[] | { results: DesignChange[] }>(
            base + "changes/",
            { org },
          ),
          platform<DesignRelease[] | { results: DesignRelease[] }>(
            base + "releases/",
            { org },
          ),
          platform<DesignConversation>(
            base + `conversation/?after=${cursor.current}`,
            { org },
          ),
          saveFlow
            ? platform<DesignSaveOperation[]>(base + "save-operations/", {
                org,
              })
            : Promise.resolve([]),
        ]);
      if (session.ai_wallet && !previewOnly) {
        try {
          const wallet = await platform<DesignAIWallet>(base + "ai-wallet/", {
            org,
          });
          if (alive.current) {
            setAiWallet(wallet);
            setAiWalletError(false);
          }
        } catch {
          if (alive.current) setAiWalletError(true);
        }
      }
      if (!alive.current) return;
      if (saveFlow) setSaveOperation(nextSaves[0] ?? null);
      setSite(nextSite);
      onWebsiteUpdated?.(nextSite);
      setAccessDenied(false);
      setChanges(items(nextChanges));
      setReleases(items(nextReleases));
      cursor.current = Math.max(cursor.current, history.cursor);
      setEvents((previous) => {
        const known = new Set(previous.map((event) => event.id));
        return [
          ...previous,
          ...history.events.filter((event) => !known.has(event.id)),
        ].sort((a, b) => a.id - b.id);
      });
      if (
        !dirtyRef.current &&
        (loadedVersion.current !== nextSite.source_version ||
          loadedPreview.current !== nextSite.preview_release)
      ) {
        const nextContent = await platform<DesignContent>(base + "content/", {
          org,
        });
        if (!alive.current || dirtyRef.current) return;
        loadedVersion.current = nextContent.source_version;
        loadedPreview.current = nextSite.preview_release;
        setContent(nextContent);
        resetDraft(nextContent.values ?? {});
      }
      setRefreshError("");
    } catch (cause) {
      if (alive.current) {
        setRefreshError(message(cause));
        if (
          cause instanceof Error &&
          "status" in cause &&
          [401, 403].includes(Number(cause.status))
        )
          setAccessDenied(true);
      }
    } finally {
      loading.current = false;
    }
  }, [base, org, saveFlow, session.ai_wallet, previewOnly, onWebsiteUpdated]);

  useEffect(() => {
    alive.current = true;
    void refresh();
    const timer = setInterval(() => {
      void refresh();
    }, 4000);
    const beforeUnload = (event: BeforeUnloadEvent) => {
      if (navigationDirty.current) {
        event.preventDefault();
        event.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", beforeUnload);
    return () => {
      alive.current = false;
      clearInterval(timer);
      window.removeEventListener("beforeunload", beforeUnload);
    };
  }, [refresh]);

  useEffect(() => {
    frameLoadAttempts.current = 0;
  }, [frameUrl]);
  useEffect(() => {
    if (!frameUrl || frameLoaded || frameLoadAttempts.current >= 2) return;
    const timer = setTimeout(() => {
      frameLoadAttempts.current += 1;
      setFrameRevision((value) => value + 1);
    }, 5000);
    return () => clearTimeout(timer);
  }, [frameUrl, frameRevision, frameLoaded]);

  const sendDraft = useCallback(() => {
    if (!frame.current?.contentWindow || !origin || !previewDigest || !content)
      return;
    frame.current.contentWindow.postMessage(
      {
        type: editable ? "im-editor:init" : "im-editor:disable",
        websiteId,
        sourceVersion: content.source_version,
        contentDigest: previewDigest,
        values:
          dirtyRef.current || currentPreview
            ? history.current.current.values
            : {},
        protocol: 2,
        editingSession,
        revision: draftRevision.current,
        acknowledgedSequence: receivedSequence.current,
        canUndo: history.current.canUndo,
        canRedo: history.current.canRedo,
      },
      origin,
    );
  }, [
    origin,
    content,
    previewDigest,
    editable,
    websiteId,
    draft,
    dirty,
    currentPreview,
    editingSession,
    historyRevision,
  ]);

  useEffect(() => {
    sendDraft();
  }, [sendDraft]);
  useEffect(() => {
    receivedSequence.current = 0;
    setEnhanced(false);
    setComposing(false);
    return () => {
      const waiter = flushWaiter.current;
      if (waiter) {
        clearTimeout(waiter.timer);
        waiter.reject(
          new Error(
            "The preview changed before saving. Your draft is retained.",
          ),
        );
        flushWaiter.current = null;
      }
      if (origin && content)
        frame.current?.contentWindow?.postMessage(
          {
            type: "im-editor:disable",
            protocol: 2,
            editingSession,
            websiteId,
            sourceVersion: content.source_version,
            contentDigest: previewDigest,
          },
          origin,
        );
    };
  }, [editingSession]);
  useEffect(() => {
    const receive = (event: MessageEvent) => {
      const value = event.data as WebsiteEditorFrameMessage;
      if (
        event.source !== frame.current?.contentWindow ||
        event.origin !== origin ||
        !value ||
        !editable ||
        value.websiteId !== websiteId ||
        value.contentDigest !== previewDigest
      )
        return;
      if ((value as { type: string }).type === "im-editor:ready") {
        sendDraft();
        return;
      }
      if (value.sourceVersion !== content?.source_version) return;
      const modern = value.protocol === 2;
      if (modern) {
        if (
          value.editingSession !== editingSession ||
          !Number.isSafeInteger(value.sequence) ||
          value.sequence !== receivedSequence.current + 1
        )
          return;
        receivedSequence.current = value.sequence;
        if (value.type === "im-editor:connected") {
          setEnhanced(true);
          return;
        }
        if (value.type === "im-editor:flushed") {
          const waiter = flushWaiter.current;
          if (waiter?.id === value.requestId) {
            clearTimeout(waiter.timer);
            flushWaiter.current = null;
            if (value.composing)
              waiter.reject(
                new Error(
                  "Finish entering text before saving. Your draft is retained.",
                ),
              );
            else waiter.resolve();
          }
          return;
        }
        if (value.type === "im-editor:composition") {
          if (
            fields.some(
              (field) =>
                field.id === value.fieldId &&
                ["text", "multiline"].includes(field.type),
            ) &&
            typeof value.active === "boolean"
          )
            setComposing(value.active);
          return;
        }
        if (value.type === "im-editor:invalid") {
          if (fields.some((field) => field.id === value.fieldId))
            setError(
              "Use plain text up to 20,000 characters. The previous valid text is retained.",
            );
          return;
        }
        if (value.type === "im-editor:history") {
          travel(value.direction);
          return;
        }
        if (value.type === "im-editor:boundary") {
          history.current.boundary();
          return;
        }
      } else if (enhanced) return;
      if (value.type === "im-editor:deselect") {
        history.current.boundary();
        setSelected("");
        setRelatedIds([]);
        setAiContext(null);
        setTab("chat");
        return;
      }
      if (!["im-editor:change", "im-editor:select"].includes(value.type))
        return;
      const item = value as {
        fieldId: string;
        value?: unknown;
        relatedFieldIds?: string[];
      };
      const field = fields.find((entry) => entry.id === item.fieldId);
      if (!field) return;
      if (value.type === "im-editor:select" || !selected) {
        setSelected(field.id);
        setAiContext(field.id);
        setTab("content");
        setPanelOpen(true);
        setToolsCollapsed(false);
      }
      setRelatedIds((previous) =>
        value.type === "im-editor:select"
          ? Array.isArray(item.relatedFieldIds)
            ? item.relatedFieldIds
                .filter((id) => fields.some((field) => field.id === id))
                .slice(0, 4)
            : []
          : selected === field.id
            ? previous
            : [],
      );
      if (
        value.type === "im-editor:change" &&
        ["text", "multiline"].includes(field.type) &&
        validWebsiteText(item.value)
      ) {
        // Input arriving during a flush must be included before its acknowledgement.
        if (history.current.change(field.id, item.value)) updateHistoryView();
      }
    };
    window.addEventListener("message", receive);
    return () => window.removeEventListener("message", receive);
  }, [
    origin,
    editable,
    websiteId,
    content,
    previewDigest,
    fields,
    sendDraft,
    enhanced,
    editingSession,
    flushing,
    selected,
    composing,
  ]);
  async function flushInput() {
    if (!enhanced) return;
    if (!frameLoaded || !origin || !content)
      throw new Error(
        "Wait for the preview to load before saving. Your draft is retained.",
      );
    await new Promise<void>((resolve, reject) => {
      const id = crypto.randomUUID();
      const timer = setTimeout(() => {
        flushWaiter.current = null;
        reject(
          new Error(
            "The preview did not confirm your latest input. Try saving again; your draft is retained.",
          ),
        );
      }, 2500);
      flushWaiter.current = { id, resolve, reject, timer };
      frame.current?.contentWindow?.postMessage(
        {
          type: "im-editor:flush",
          protocol: 2,
          editingSession,
          websiteId,
          sourceVersion: content.source_version,
          contentDigest: previewDigest,
          requestId: id,
        },
        origin,
      );
    });
  }

  useEffect(() => {
    const controller = new AbortController();
    let timer: ReturnType<typeof setTimeout>;
    async function connect() {
      try {
        const response = await fetch(
          `/platform/api/${base}conversation/?after=${cursor.current}`,
          {
            credentials: "same-origin",
            headers: {
              Accept: "text/event-stream",
              "X-Org-ID": String(org),
            },
            signal: controller.signal,
          },
        );
        if (!response.ok) throw new Error("stream unavailable");
        const text = await response.text();
        const received = text
          .split("\n")
          .filter((line) => line.startsWith("data: "))
          .map((line) => JSON.parse(line.slice(6)) as DesignConversationEvent);
        if (!controller.signal.aborted && received.length) {
          cursor.current = Math.max(
            cursor.current,
            ...received.map((event) => event.id),
          );
          setEvents((previous) => {
            const known = new Set(previous.map((event) => event.id));
            return [
              ...previous,
              ...received.filter((event) => !known.has(event.id)),
            ].sort((a, b) => a.id - b.id);
          });
        }
      } catch {
        /* The regular status poll remains the fallback. */
      }
      if (!controller.signal.aborted)
        timer = setTimeout(() => {
          void connect();
        }, 2500);
    }
    timer = setTimeout(() => {
      void connect();
    }, 2500);
    return () => {
      controller.abort();
      clearTimeout(timer);
    };
  }, [base, org]);

  useEffect(() => {
    if (!publishMenu) return;
    publishRoot.current
      ?.querySelector<HTMLButtonElement>('[role="menuitem"]')
      ?.focus();
    const close = (event: PointerEvent) => {
      if (!publishRoot.current?.contains(event.target as Node))
        setPublishMenu(false);
    };
    const key = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        setPublishMenu(false);
        publishTrigger.current?.focus();
      }
      if (event.key === "Tab") setPublishMenu(false);
    };
    document.addEventListener("pointerdown", close);
    document.addEventListener("keydown", key);
    return () => {
      document.removeEventListener("pointerdown", close);
      document.removeEventListener("keydown", key);
    };
  }, [publishMenu]);

  function keyFor(value: unknown) {
    const signature = JSON.stringify(value);
    if (pending.current?.signature !== signature)
      pending.current = { signature, key: crypto.randomUUID() };
    return pending.current.key;
  }
  async function perform(
    action: () => Promise<void>,
    permissionFailure = true,
  ) {
    if (pendingAction.current) return false;
    pendingAction.current = true;
    setBusy(true);
    setError("");
    try {
      await action();
      pending.current = null;
      await refresh();
      return true;
    } catch (cause) {
      setError(message(cause));
      if (
        permissionFailure &&
        cause instanceof Error &&
        "status" in cause &&
        [401, 403].includes(Number(cause.status))
      )
        setAccessDenied(true);
      if (!permissionFailure) await refresh();
      return false;
    } finally {
      pendingAction.current = false;
      if (alive.current) setBusy(false);
    }
  }
  function discard() {
    dirtyRef.current = false;
    resetDraft(content?.values ?? {});
    loadedVersion.current = -1;
    void refresh();
  }
  async function save() {
    if (
      !content ||
      !site ||
      !canEdit ||
      flushing ||
      savePending ||
      (dirty && !editable)
    )
      return;
    if (saveFlow && site.live_release && !canPublish) return;
    setFlushing(true);
    setError("");
    try {
      if (dirty) await flushInput();
      const snapshot = history.current.current;
      const operations = Object.keys(snapshot.values)
        .filter((id) => snapshot.values[id] !== content.values?.[id])
        .map((id) =>
          snapshot.assets[id]
            ? { id, asset_id: snapshot.assets[id].id }
            : { id, value: snapshot.values[id] },
        );
      if (!operations.length && !(saveFlow && hasPreparedChanges)) return;
      await perform(async () => {
        const body = {
          expected_source_version: content.source_version,
          ...(saveFlow ? { expected_live_release: site.live_release } : {}),
          ...(operations.length
            ? { operations }
            : { release_id: currentRelease?.id }),
        };
        const result = await platform<DesignSaveOperation>(
          base + (saveFlow ? "save/" : "content/"),
          {
            method: saveFlow ? "POST" : "PATCH",
            org,
            csrf: session.csrf_token,
            body: { ...body, request_key: keyFor(body) },
          },
        );
        if (saveFlow) setSaveOperation(result);
        dirtyRef.current = false;
        loadedVersion.current = -1;
        resetDraft({});
        setHistorical(null);
      });
    } catch (cause) {
      setError(message(cause));
      if (
        cause instanceof Error &&
        "status" in cause &&
        [401, 403].includes(Number(cause.status))
      )
        setAccessDenied(true);
    } finally {
      if (alive.current) setFlushing(false);
    }
  }
  async function submit() {
    if (
      previewOnly ||
      !prompt.trim() ||
      !aiAllowed ||
      dirty ||
      flushing ||
      !site ||
      !canEdit ||
      publicationBusy ||
      savePending ||
      attachments.files.some((file) => file.status !== "ready")
    )
      return;
    const replying = active?.status === "NEEDS_INPUT";
    const instructions = `${prompt}\nCurrent page: ${page}${aiContext && fields.some((field) => field.id === aiContext) ? `\nSelected content field: ${aiContext}` : ""}`;
    await perform(async () => {
      const body = {
        ...(replying
          ? { message: prompt }
          : {
              instructions,
              expected_source_version: site.source_version,
            }),
        attachments: references.map((reference) => reference.id),
        request_key: keyFor({
          prompt,
          references,
          selected: aiContext,
          page,
          reply: replying ? active.id : null,
        }),
      };
      await platform(
        base + (replying ? `changes/${active.id}/reply/` : "design-revisions/"),
        { method: "POST", org, csrf: session.csrf_token, body },
      );
      setPrompt("");
      attachments.clear();
      setHistorical(null);
    }, false);
  }
  async function upload(file: File, fieldId?: string) {
    if (
      !["image/jpeg", "image/png", "image/webp"].includes(file.type) ||
      file.size > 10 * 1024 * 1024
    ) {
      setError("Choose a JPG, PNG or WebP image up to 10 MiB.");
      return;
    }
    await perform(async () => {
      const asset = await uploadDesignAsset(
        websiteId,
        file,
        org,
        session.csrf_token,
      );
      if (fieldId) {
        const url = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(String(reader.result));
          reader.onerror = () =>
            reject(new Error("This image could not be read."));
          reader.readAsDataURL(file);
        });
        if (history.current.change(fieldId, url, { id: asset.id, url }, true))
          updateHistoryView();
      }
    });
  }
  function back() {
    if (
      !navigationDirty.current ||
      window.confirm("Leave with unsaved edits or an unsent message?")
    ) {
      dirtyRef.current = false;
      onBack();
    }
  }

  const latest = changes[0];
  const checkingFailed =
    latest?.status === "FAILED" &&
    currentRelease?.source_version !== site?.source_version;
  const saveStatus = dirty
    ? "Unsaved changes"
    : active
      ? active.kind === "manual"
        ? "Saving/checking"
        : labels[active.status]
      : latest?.kind === "ai" && latest.status === "FAILED"
        ? "Could not complete this change"
        : latest?.kind === "ai" && latest.status === "CANCELLED"
          ? "Stopped"
          : checkingFailed
            ? "Checking failed"
            : site && currentRelease?.source_version !== site.source_version
              ? "Changes need checking"
              : site
                ? "Saved"
                : "Loading…";
  const publishDisabled =
    previewOnly ||
    busy ||
    publicationBusy ||
    !!active ||
    dirty ||
    flushing ||
    checkingFailed ||
    currentRelease?.id !== site?.preview_release ||
    currentRelease?.source_version !== site?.source_version ||
    !currentRelease?.review_url ||
    (site?.publication_state === "published" && !dirty);
  const hasPreparedChanges = Boolean(
    saveFlow &&
    currentRelease &&
    site &&
    currentRelease.source_version === site.source_version &&
    currentRelease.id === site.preview_release &&
    currentRelease.id !== saveOperation?.release_id &&
    (site.publication_state === "unpublished_changes" ||
      (latest?.kind === "ai" && latest.status === "READY")),
  );
  async function retrySave() {
    if (!saveOperation || !saveFlow || dirty) return;
    await perform(async () => {
      const body = {
        expected_source_version: saveOperation.source_version,
        expected_live_release: saveOperation.expected_live_release_id,
      };
      const result = await platform<DesignSaveOperation>(
        base + `save-operations/${saveOperation.id}/retry/`,
        {
          method: "POST",
          org,
          csrf: session.csrf_token,
          body: {
            ...body,
            request_key: keyFor({
              retry: saveOperation.id,
              attempt: saveOperation.attempt,
              ...body,
            }),
          },
        },
      );
      setSaveOperation(result);
    });
  }
  const visibleEvents = events.filter((event) => {
    const kind =
      event.change_kind ??
      changes.find((change) => change.id === event.change_id)?.kind ??
      event.payload.kind;
    return (
      kind !== "manual" &&
      ["user", "assistant"].includes(event.kind) &&
      !!event.payload.text
    );
  });
  const cancel = () =>
    void perform(async () => {
      if (active)
        await platform(base + `changes/${active.id}/cancel/`, {
          method: "POST",
          org,
          csrf: session.csrf_token,
        });
    });
  return (
    <main
      className={css.editor}
      onDragOver={(event) => {
        if (event.dataTransfer.types.includes("Files")) event.preventDefault();
      }}
      onDrop={(event) => {
        if (
          event.dataTransfer.types.includes("Files") &&
          !(
            event.target instanceof HTMLInputElement &&
            event.target.type === "file"
          )
        )
          event.preventDefault();
      }}
    >
      <header className={css.header}>
        {navigationControl ?? (
          <Button
            onClick={back}
            aria-label="Back to Pages"
            title="Back to Pages"
          >
            <ArrowLeft size={17} />
            Pages
          </Button>
        )}
        <Button
          ref={panelTrigger}
          className={`${css.headerIcon} ${css.headerChatToggle}`}
          data-tooltip={
            (narrow ? panelOpen && !toolsCollapsed : !toolsCollapsed)
              ? "Collapse chat"
              : "Open chat"
          }
          aria-controls="website-tools"
          aria-label={
            (narrow ? panelOpen && !toolsCollapsed : !toolsCollapsed)
              ? "Hide chat and tools"
              : "Show chat and tools"
          }
          aria-expanded={
            narrow ? panelOpen && !toolsCollapsed : !toolsCollapsed
          }
          onClick={() => {
            if (narrow) {
              setToolsCollapsed(false);
              setPanelOpen((value) => !value);
            } else setToolsCollapsed((value) => !value);
          }}
        >
          {(narrow ? !panelOpen || toolsCollapsed : toolsCollapsed) ? (
            <PanelLeftOpen size={16} />
          ) : (
            <PanelLeftClose size={16} />
          )}
        </Button>
        <div className={css.identity}>
          {onPageChange ? (
            <div
              className={css.pagePickerRoot}
              ref={pagePickerRoot}
              onBlur={(event) => {
                if (
                  !event.currentTarget.contains(
                    event.relatedTarget as Node | null,
                  )
                )
                  setPagePickerOpen(false);
              }}
              onKeyDown={(event) => {
                if (event.key === "Escape" && pagePickerOpen) {
                  event.preventDefault();
                  setPagePickerOpen(false);
                  pagePickerTrigger.current?.focus();
                }
              }}
            >
              <Button
                ref={pagePickerTrigger}
                className={css.pagePickerButton}
                aria-label={`Switch page: ${pageName(page)}`}
                aria-haspopup="menu"
                aria-expanded={pagePickerOpen}
                onClick={() => {
                  setPageSearch("");
                  setPagePickerOpen((value) => !value);
                }}
                onKeyDown={(event) => {
                  if (event.key === "ArrowDown") {
                    event.preventDefault();
                    setPageSearch("");
                    setPagePickerOpen(true);
                  }
                }}
              >
                <span>{pageName(page)}</span>
                <ChevronDown size={14} />
              </Button>
              {pagePickerOpen && (
                <div className={css.pageMenu}>
                  {pageOptions.length > 8 && (
                    <Input
                      aria-label="Find a page"
                      placeholder="Find a page…"
                      value={pageSearch}
                      onChange={(event) => setPageSearch(event.target.value)}
                      onKeyDown={(event) => {
                        if (event.key === "ArrowDown") {
                          event.preventDefault();
                          pagePickerRoot.current
                            ?.querySelector<HTMLElement>(
                              '[role="menuitemradio"]',
                            )
                            ?.focus();
                        }
                      }}
                    />
                  )}
                  <div
                    role="menu"
                    aria-label="Website pages"
                    onKeyDown={(event) => {
                      if (
                        !["ArrowDown", "ArrowUp", "Home", "End"].includes(
                          event.key,
                        )
                      )
                        return;
                      event.preventDefault();
                      const choices = Array.from(
                        event.currentTarget.querySelectorAll<HTMLElement>(
                          '[role="menuitemradio"]',
                        ),
                      );
                      const current = choices.indexOf(
                        document.activeElement as HTMLElement,
                      );
                      const index =
                        event.key === "Home"
                          ? 0
                          : event.key === "End"
                            ? choices.length - 1
                            : (current +
                                (event.key === "ArrowDown" ? 1 : -1) +
                                choices.length) %
                              choices.length;
                      choices[index]?.focus();
                    }}
                  >
                    {pageOptions
                      .filter((path) =>
                        `${pageName(path)} ${path}`
                          .toLowerCase()
                          .includes(pageSearch.toLowerCase()),
                      )
                      .map((path) => (
                        <button
                          key={path}
                          type="button"
                          role="menuitemradio"
                          aria-checked={path === page}
                          onClick={() => {
                            setPagePickerOpen(false);
                            pagePickerTrigger.current?.focus();
                            if (path !== page) onPageChange(path);
                          }}
                        >
                          <span>{pageName(path)}</span>
                          {path === page && <Check size={15} />}
                        </button>
                      ))}
                  </div>
                  {!pageOptions.some((path) =>
                    `${pageName(path)} ${path}`
                      .toLowerCase()
                      .includes(pageSearch.toLowerCase()),
                  ) && <p>No pages found</p>}
                </div>
              )}
            </div>
          ) : (
            <h1>{pageName(page)}</h1>
          )}
          {(active || publicationBusy || busy || flushing) && (
            <div className={css.headerStatus}>
              <span role="status">
                {active && <span className={css.spinner} />}
                {publicationBusy ? "Publishing…" : saveStatus}
              </span>
              {active && (
                <Button disabled={busy} onClick={cancel}>
                  Cancel
                </Button>
              )}
            </div>
          )}
        </div>
        <div className={css.toolbar} role="group" aria-label="Preview controls">
          <Button
            aria-label="Desktop"
            data-tooltip="Desktop"
            aria-pressed={!mobile}
            onClick={() => setMobile(false)}
          >
            <Monitor size={16} />
          </Button>
          <Button
            aria-label="Mobile"
            data-tooltip="Mobile"
            aria-pressed={mobile}
            onClick={() => setMobile(true)}
          >
            <Smartphone size={16} />
          </Button>
          <Button
            aria-label="Refresh preview"
            data-tooltip="Refresh preview"
            disabled={dirty}
            onClick={() => {
              frameLoadAttempts.current = 0;
              setFrameRevision((value) => value + 1);
            }}
          >
            <RotateCw size={15} />
          </Button>
          {site?.review_url && (
            <a
              href={new URL(page, site.review_url).href}
              target="_blank"
              rel="noreferrer"
              aria-label="Open preview in new tab"
              data-tooltip="Open preview in new tab"
            >
              <ExternalLink size={16} />
            </a>
          )}
        </div>
        <div className={css.headerActions}>
          {unifiedSave && (
            <Button
              variant="primary"
              title={
                previewOnly
                  ? "Save and check the local preview. Live publishing is disconnected."
                  : undefined
              }
              disabled={
                (!dirty && !hasPreparedChanges) ||
                (dirty && !editable) ||
                !canEdit ||
                (saveFlow && !!site?.live_release && !canPublish) ||
                busy ||
                flushing ||
                composing ||
                savePending
              }
              onClick={() => void save()}
            >
              {flushing || busy
                ? "Saving…"
                : saveOperation?.status === "PUBLISHING" || publicationBusy
                  ? "Publishing…"
                  : active || saveOperation?.status === "CHECKING"
                    ? "Checking…"
                    : "Save changes"}
            </Button>
          )}
          {!unifiedSave && canPublish && (
            <div className={css.publishSplit} ref={publishRoot}>
              <Button
                variant="primary"
                disabled={publishDisabled}
                onClick={() =>
                  void perform(async () => {
                    await platform(base + "publish/", {
                      method: "POST",
                      org,
                      csrf: session.csrf_token,
                      body: {
                        release_id: currentRelease?.id,
                        expected_live_release: site?.live_release,
                        request_key: keyFor({
                          publish: site?.preview_release,
                          live: site?.live_release,
                        }),
                      },
                    });
                  })
                }
              >
                Publish
              </Button>
              <Button
                ref={publishTrigger}
                aria-label="Publishing options"
                aria-haspopup="menu"
                onKeyDown={(event) => {
                  if (event.key === "ArrowDown") {
                    event.preventDefault();
                    setPublishMenu(true);
                  }
                }}
                aria-expanded={publishMenu}
                disabled={previewOnly || busy || publicationBusy}
                onClick={() => setPublishMenu((value) => !value)}
              >
                ▾
              </Button>
              {publishMenu && (
                <div
                  className={css.publishMenu}
                  role="menu"
                  onKeyDown={(event) => {
                    if (event.key === "Escape") setPublishMenu(false);
                  }}
                >
                  <Button
                    role="menuitem"
                    onClick={() => {
                      setPublishMenu(false);
                      setError("");
                      setRestoreOpen(true);
                    }}
                  >
                    Restore previous version…
                  </Button>
                </div>
              )}
            </div>
          )}
          {onOpenHistory && (
            <div
              className={css.moreActions}
              ref={actionsRoot}
              onBlur={(event) => {
                if (
                  !event.currentTarget.contains(
                    event.relatedTarget as Node | null,
                  )
                )
                  setActionsOpen(false);
              }}
              onKeyDown={(event) => {
                if (event.key === "Escape") {
                  setActionsOpen(false);
                  actionsTrigger.current?.focus();
                }
              }}
            >
              <Button
                ref={actionsTrigger}
                className={css.historyButton}
                aria-label="More actions"
                title="More actions"
                aria-haspopup="menu"
                aria-expanded={actionsOpen}
                onClick={() => setActionsOpen((value) => !value)}
                onKeyDown={(event) => {
                  if (event.key === "ArrowDown") {
                    event.preventDefault();
                    setActionsOpen(true);
                  }
                }}
              >
                <MoreHorizontal size={18} />
              </Button>
              {actionsOpen && (
                <div
                  className={css.moreMenu}
                  role="menu"
                  aria-label="More actions"
                >
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      setActionsOpen(false);
                      actionsTrigger.current?.focus();
                      onOpenHistory();
                    }}
                  >
                    <History size={16} />
                    Version history
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </header>
      {site?.hosting?.editing_enabled === false && (
        <div role="status" className={css.notice}>
          <span>
            Editing is paused. Check hosting billing in Settings to restore
            access.
          </span>
          {onOpenSettings && (
            <Button variant="secondary" onClick={onOpenSettings}>
              Settings
            </Button>
          )}
        </div>
      )}
      {site?.publication?.status === "FAILED" && !error && (
        <div role="alert" className={css.error}>
          Publishing failed. Your live website has not changed.
        </div>
      )}
      {saveFlow && saveOperation?.status === "FAILED" && (
        <div role="alert" className={css.error}>
          {saveOperation.release_id
            ? "Publishing failed. Your previous live website is unchanged."
            : "Checking failed. Your saved work is retained."}
          <Button
            onClick={() => void retrySave()}
            disabled={
              busy ||
              dirty ||
              !canEdit ||
              saveOperation.created_by_id !== session.user.id ||
              (saveOperation.publish_requested && !canPublish)
            }
          >
            Retry save
          </Button>
        </div>
      )}
      {(error || refreshError) && !restoreOpen && (
        <div role="alert" className={css.error}>
          {error || refreshError}
        </div>
      )}
      <div
        className={`${css.grid} ${toolsCollapsed ? css.toolsCollapsed : ""}`}
      >
        <aside
          id="website-tools"
          className={`${css.sidebar} ${panelOpen ? css.panelOpen : ""} ${sheetExpanded ? css.sheetExpanded : ""}`}
          aria-label="Website tools"
          data-expanded={sheetExpanded}
          data-dragging={sheetGesture.current !== null || undefined}
          style={
            narrow && sheetOffset
              ? {
                  height: `clamp(100px, calc(${sheetExpanded ? "90dvh" : "min(60dvh, 600px)"} + ${-sheetOffset}px), 90dvh)`,
                }
              : undefined
          }
          onKeyDown={(event) => {
            if (event.key === "Escape" && panelOpen) {
              event.preventDefault();
              setPanelOpen(false);
              panelTrigger.current?.focus();
            }
          }}
        >
          <button
            type="button"
            className={css.sheetHandle}
            aria-label="Close chat and tools"
            aria-description="Drag up to expand, or down to reduce or close. Arrow keys resize the panel."
            onKeyDown={(event) => {
              if (event.key === "ArrowUp") {
                event.preventDefault();
                setSheetExpanded(true);
              } else if (event.key === "ArrowDown") {
                event.preventDefault();
                if (sheetExpanded) setSheetExpanded(false);
                else {
                  setPanelOpen(false);
                  panelTrigger.current?.focus();
                }
              }
            }}
            onPointerDown={(event) => {
              if (
                !narrow ||
                !event.isPrimary ||
                (event.pointerType === "mouse" && event.button !== 0)
              )
                return;
              sheetMoved.current = false;
              sheetGesture.current = {
                id: event.pointerId,
                y: event.clientY,
                time: event.timeStamp,
              };
              event.currentTarget.setPointerCapture?.(event.pointerId);
            }}
            onPointerMove={(event) => {
              const gesture = sheetGesture.current;
              if (!gesture || gesture.id !== event.pointerId) return;
              const offset = event.clientY - gesture.y;
              if (Math.abs(event.clientY - gesture.y) > 6)
                sheetMoved.current = true;
              setSheetOffset(offset);
            }}
            onPointerUp={(event) => {
              const gesture = sheetGesture.current;
              if (!gesture || gesture.id !== event.pointerId) return;
              const distance = event.clientY - gesture.y;
              const velocity =
                distance / Math.max(1, event.timeStamp - gesture.time);
              if (distance <= -72 || (distance <= -24 && velocity < -0.5)) {
                setSheetExpanded(true);
              } else if (distance >= 72 || (distance >= 24 && velocity > 0.5)) {
                if (sheetExpanded) setSheetExpanded(false);
                else {
                  setPanelOpen(false);
                  panelTrigger.current?.focus();
                }
              }
              sheetGesture.current = null;
              setSheetOffset(0);
              if (event.currentTarget.hasPointerCapture?.(event.pointerId))
                event.currentTarget.releasePointerCapture(event.pointerId);
            }}
            onPointerCancel={() => {
              sheetGesture.current = null;
              setSheetOffset(0);
            }}
            onClick={(event) => {
              if (!sheetMoved.current || event.detail === 0) {
                setPanelOpen(false);
                panelTrigger.current?.focus();
              }
            }}
          >
            <span aria-hidden="true" />
          </button>
          <div
            className={css.chatPane}
            ref={chatPane}
            hidden={tab !== "chat"}
            aria-label="Chat"
          >
            <EditorChat
              active={tab === "chat"}
              events={visibleEvents}
              prompt={prompt}
              onPrompt={setPrompt}
              onSend={() => void submit()}
              disabled={
                !canEdit ||
                savePending ||
                busy ||
                (!!active && active.status !== "NEEDS_INPUT")
              }
              canSend={
                !!prompt.trim() &&
                canEdit &&
                aiAllowed &&
                !busy &&
                !dirty &&
                !flushing &&
                !publicationBusy &&
                !savePending &&
                (!active || active.status === "NEEDS_INPUT") &&
                attachments.files.every((file) => file.status === "ready")
              }
              attachments={attachments}
              pageLabel={page === "/" ? "Home" : page}
              workingLabel={
                active && active.status !== "NEEDS_INPUT"
                  ? labels[active.status]
                  : undefined
              }
              creditNotice={
                session.ai_wallet &&
                aiWallet?.reason === "insufficient_credits" &&
                !aiAllowed
                  ? "No website AI credits remaining. Manual editing is available."
                  : undefined
              }
              unavailableReason={
                previewOnly
                  ? "AI is disconnected in this local preview."
                  : session.ai_wallet && !aiAllowed
                    ? aiWalletError
                      ? "Could not check website AI credits. Try refreshing."
                      : aiWallet?.reason === "insufficient_credits"
                        ? "No website AI credits remaining. Manual editing is available."
                        : aiWallet?.reason === "hosting_paused"
                          ? "AI editing is paused until hosting is restored."
                          : aiWallet?.reason === "view_only"
                            ? "You have view-only access."
                            : aiWallet
                              ? "This AI change cannot be resumed in this account."
                              : "Checking website AI credits…"
                    : undefined
              }
              context={
                aiContext
                  ? fields.find((field) => field.id === aiContext)?.type ===
                    "image"
                    ? "Selected image"
                    : fields.find((field) => field.id === aiContext)?.label
                  : undefined
              }
              onClearContext={() => setAiContext(null)}
              question={active?.question}
              draftBlocked={dirty}
            />
          </div>
          <section
            className={css.inspector}
            hidden={tab !== "content"}
            aria-label="Content inspector"
            onKeyDown={inspectorKey}
            onBlur={() => history.current.boundary()}
          >
            <div className={css.inspectorHeading}>
              <Button
                aria-label="Close content inspector"
                onClick={() => {
                  setTab("chat");
                  requestAnimationFrame(() =>
                    chatPane.current?.querySelector("textarea")?.focus(),
                  );
                }}
                title="Back to chat"
              >
                <ArrowLeft size={17} />
              </Button>
              <h2>
                {selectedField?.type === "image"
                  ? "Edit image"
                  : selectedField?.type === "link"
                    ? "Edit link"
                    : "Edit text"}
              </h2>
            </div>
            <div className={css.fields}>
              {!content?.enabled ? (
                <p>
                  This website supports AI revisions. Manual content editing has
                  not been enabled yet.
                </p>
              ) : (
                <>
                  {!currentPreview && (
                    <p className={css.notice}>
                      Showing the last checked version. The current source has
                      not passed a new check yet.
                    </p>
                  )}
                  {site?.source_version !== content.source_version && dirty && (
                    <p role="alert">
                      The website changed elsewhere. Your draft is retained;
                      discard it to load the latest version.
                    </p>
                  )}
                  {selectedField &&
                    (selectedField.type === "image" ? (
                      <ImageField
                        value={draft[selected] ?? ""}
                        previewOrigin={origin}
                        uploadedUrl={
                          history.current.current.assets[selected]?.url
                        }
                        images={content.images ?? []}
                        disabled={!editable || flushing}
                        uploading={busy}
                        onReplace={(file) => void upload(file, selected)}
                        onChoose={(value) => editField(selected, value)}
                      />
                    ) : (
                      <div data-draft-field className={css.textEditor}>
                        {selectedField.type === "link" ? (
                          <label>
                            Link destination
                            <Input
                              value={draft[selected] ?? ""}
                              disabled={!editable || flushing}
                              onChange={(event) =>
                                editField(selected, event.target.value)
                              }
                            />
                          </label>
                        ) : (
                          <Textarea
                            aria-label="Selected text"
                            rows={6}
                            value={draft[selected] ?? ""}
                            disabled={!editable || flushing}
                            onChange={(event) =>
                              editField(
                                selected,
                                selectedField.type === "text"
                                  ? event.target.value.replace(/[\r\n]+/g, " ")
                                  : event.target.value,
                              )
                            }
                          />
                        )}
                      </div>
                    ))}
                  {relatedIds
                    .filter((id) => id !== selected)
                    .map((id) => {
                      const field = fields.find((item) => item.id === id)!;
                      const imageDescription =
                        selectedField?.type === "image" &&
                        field.type !== "link";
                      const Control = imageDescription ? Textarea : Input;
                      return (
                        <label key={id} data-draft-field>
                          {field.type === "link"
                            ? "Link destination"
                            : imageDescription
                              ? "Image description (alt text)"
                              : "Text"}
                          <Control
                            value={draft[id] ?? ""}
                            disabled={!editable || flushing}
                            onChange={(event) =>
                              editField(
                                id,
                                field.type === "text"
                                  ? event.target.value.replace(/[\r\n]+/g, " ")
                                  : event.target.value,
                              )
                            }
                          />
                        </label>
                      );
                    })}
                </>
              )}
            </div>
          </section>
          {dirty && (
            <div className={css.actions}>
              {!unifiedSave && (
                <Button
                  variant="primary"
                  disabled={!dirty || !editable || flushing}
                  onClick={() => void save()}
                >
                  Save changes
                </Button>
              )}
              <Button
                disabled={!dirty || busy || flushing || composing}
                onClick={discard}
              >
                Discard changes
              </Button>
            </div>
          )}
        </aside>
        <section className={css.preview} aria-label="Website preview">
          {(historical ||
            active ||
            (currentRelease &&
              currentRelease.source_version !== site?.source_version)) && (
            <p className={css.muted}>
              {historical
                ? "Historical review — read only."
                : active
                  ? "Last checked preview; changes are in progress."
                  : currentRelease?.source_version !== site?.source_version
                    ? "Last checked preview; the current source has changes awaiting a checked release."
                    : "Latest checked preview."}
            </p>
          )}
          {content?.enabled === false && (
            <p className={css.notice}>
              {content.contract_error
                ? "The AI draft needs repair before direct editing. Continue in chat; the last checked preview remains available."
                : "Use chat for changes to this website. Direct text editing has not been configured yet."}
            </p>
          )}
          <div className={css.canvas}>
            {frameUrl && !frameLoaded && (
              <p role="status" className={css.previewLoading}>
                {frameLoadAttempts.current < 2
                  ? "Loading your saved preview…"
                  : "The saved preview is taking longer to load. Use Refresh preview to try again."}
              </p>
            )}
            {frameUrl ? (
              <iframe
                key={frameKey}
                ref={frame}
                title="Your website preview"
                src={frameUrl}
                onLoad={() => {
                  setLoadedFrameKey(frameKey);
                  setFrameEpoch((value) => value + 1);
                }}
                className={mobile ? css.mobile : ""}
                sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
                referrerPolicy="no-referrer"
              />
            ) : (
              <p className={css.empty}>
                Your checked preview will appear here.
              </p>
            )}
          </div>
        </section>
      </div>
      {restoreOpen && canPublish && (
        <PublishedVersions
          websiteId={websiteId}
          org={org}
          dirty={dirty}
          blocked={busy || flushing || publicationBusy || !!active || composing}
          error={error || refreshError}
          onClose={() => setRestoreOpen(false)}
          onSave={() => void save()}
          onDiscard={discard}
          onRestore={(releaseId) =>
            perform(async () => {
              if (dirtyRef.current || active || !site)
                throw new Error("Save or discard your edits before restoring.");
              await platform(base + "restore-draft/", {
                method: "POST",
                org,
                csrf: session.csrf_token,
                body: {
                  release_id: releaseId,
                  expected_source_version: site.source_version,
                  request_key: keyFor({
                    restoreDraft: releaseId,
                    version: site.source_version,
                  }),
                },
              });
              dirtyRef.current = false;
              loadedVersion.current = -1;
              resetDraft({});
              setSelected("");
              setTab("chat");
              setAiContext(null);
            })
          }
        />
      )}
    </main>
  );
}

function ImageField({
  value,
  previewOrigin,
  uploadedUrl,
  images,
  disabled,
  uploading,
  onReplace,
  onChoose,
}: {
  value: string;
  previewOrigin: string | null;
  uploadedUrl?: string;
  images: string[];
  disabled: boolean;
  uploading: boolean;
  onReplace: (file: File) => void;
  onChoose: (value: string) => void;
}) {
  const picker = useRef<HTMLInputElement>(null);
  const [failed, setFailed] = useState(false);
  const imageUrl =
    uploadedUrl === value
      ? uploadedUrl
      : previewOrigin && value.startsWith("/") && !value.startsWith("//")
        ? new URL(value, previewOrigin).href
        : undefined;
  useEffect(() => setFailed(false), [imageUrl]);
  return (
    <div className={css.imageField}>
      <div className={css.imagePreview}>
        {imageUrl && !failed ? (
          <img
            src={imageUrl}
            alt="Selected website image"
            onError={() => setFailed(true)}
          />
        ) : (
          <span>Image preview unavailable</span>
        )}
      </div>
      <Button disabled={disabled} onClick={() => picker.current?.click()}>
        {uploading ? "Replacing image…" : "Replace image"}
      </Button>
      <input
        ref={picker}
        hidden
        type="file"
        aria-label="Replace image file"
        accept="image/jpeg,image/png,image/webp"
        disabled={disabled}
        onChange={(event) => {
          const file = event.target.files?.[0];
          if (file) onReplace(file);
          event.target.value = "";
        }}
      />
      <p className={css.muted}>JPG, PNG or WebP · up to 10 MiB</p>
      {images.length > 1 && (
        <details className={css.existingImages}>
          <summary>
            <span>Choose from website images</span>
            <ChevronDown size={15} />
          </summary>
          <div className={css.imageChoices}>
            {images.map((path) => (
              <Button
                key={path}
                disabled={disabled}
                aria-label={`Use ${path.split("/").pop()}`}
                aria-pressed={value === path}
                onClick={() => onChoose(path)}
              >
                <img
                  src={
                    previewOrigin &&
                    path.startsWith("/") &&
                    !path.startsWith("//")
                      ? new URL(path, previewOrigin).href
                      : undefined
                  }
                  alt={path.split("/").pop() ?? "Website image"}
                  loading="lazy"
                />
              </Button>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}
